/**
 * ExposureAnalysisPanel.jsx - Web context exposure analysis component
 * Displays comparison between exposed and control groups
 * Extracted from Analysis.jsx
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Globe } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const ExposureAnalysisPanel = ({ data }) => {
  if (!data || !data.has_exposure_analysis) return null;
  
  const { 
    exposure_rate, 
    exposed_count, 
    unexposed_count, 
    exposed_distribution, 
    unexposed_distribution, 
    exposed_mean_score, 
    unexposed_mean_score, 
    response_scale = [] 
  } = data;
  
  const meanDiff = ((exposed_mean_score - unexposed_mean_score) * 100).toFixed(1);
  
  const chartData = response_scale.map(opt => ({
    name: opt,
    'Exposed': exposed_distribution[opt] || 0,
    'Control': unexposed_distribution[opt] || 0,
  }));

  const exposedTotal = Object.values(exposed_distribution).reduce((s, v) => s + v, 0);
  const controlTotal = Object.values(unexposed_distribution).reduce((s, v) => s + v, 0);
  const pctData = response_scale.map(opt => ({
    name: opt,
    'Exposed %': exposedTotal > 0 ? +((exposed_distribution[opt] || 0) / exposedTotal * 100).toFixed(1) : 0,
    'Control %': controlTotal > 0 ? +((unexposed_distribution[opt] || 0) / controlTotal * 100).toFixed(1) : 0,
  }));

  return (
    <div className="space-y-6" data-testid="exposure-analysis-panel">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">Web Context Exposure Analysis</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-muted-foreground">Exposure Rate</p>
              <p className="text-xl font-bold text-blue-700">{(exposure_rate * 100).toFixed(0)}%</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-muted-foreground">Exposed</p>
              <p className="text-xl font-bold">{exposed_count}</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-muted-foreground">Control</p>
              <p className="text-xl font-bold">{unexposed_count}</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-muted-foreground">Mean Score Diff</p>
              <p className={cn(
                "text-xl font-bold", 
                parseFloat(meanDiff) > 0 ? "text-positive-soft-foreground" : parseFloat(meanDiff) < 0 ? "text-negative-soft-foreground" : "text-slate-600"
              )}>
                {parseFloat(meanDiff) > 0 ? '+' : ''}{meanDiff}pp
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border">
            {Math.abs(parseFloat(meanDiff)) < 2
              ? "Web context had minimal effect on the mean opinion. The information may have been already internalized."
              : parseFloat(meanDiff) > 0
                ? `Exposed agents shifted +${meanDiff}pp more positive. Web context had a measurable positive influence.`
                : `Exposed agents shifted ${meanDiff}pp more negative. Web context had a measurable negative influence.`}
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Response Distribution (Count)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Exposed" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Control" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Response Distribution (%)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={pctData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Exposed %" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Control %" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExposureAnalysisPanel;
